import React from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { useTranslation } from 'react-i18next';

const QCMHelp = ({ stepData }) => {
    const { t } = useTranslation();
    const { currentQCM } = useQCM();

    // Support pour les données d'étapes funnel QCM ou QCM normaux  
    const helpSource = stepData ? stepData : currentQCM;

    return (
        <div className="space-y-6">
            {/* General Help */}
            <div className="bg-gray-700 rounded-lg p-4">
                <h2 className="text-lg font-semibold text-blue-400 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('how_to_answer')}
                </h2>
                
                <div className="space-y-4 text-gray-300">
                    <div>
                        <h3 className="font-semibold text-green-400 mb-2">
                            {t('step')} 1: {t('read_carefully')}
                        </h3>
                        <p>{t('read_theme_context_question')}</p>
                    </div>

                    <div>
                        <h3 className="font-semibold text-yellow-400 mb-2">
                            {t('step')} 2: {t('write_your_thoughts')}
                        </h3>
                        <p>{t('write_initial_answer_desc')}</p>
                    </div>

                    <div>
                        <h3 className="font-semibold text-blue-400 mb-2">
                            {t('step')} 3: {t('select_option')}
                        </h3>
                        <p>{t('select_option_with_confidence')}</p>
                    </div>

                    <div>
                        <h3 className="font-semibold text-purple-400 mb-2">
                            {t('step')} 4: {t('learn_from_solution')}
                        </h3>
                        <p>{t('review_explanation_tips')}</p>
                    </div>
                </div>
            </div>

            {/* Confidence Levels Guide */}
            <div className="bg-gray-700 rounded-lg p-4">
                <h2 className="text-lg font-semibold text-cyan-400 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    {t('confidence_guide')}
                </h2>
                
                <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                        <div className="w-4 h-4 bg-red-500 rounded-full mt-1"></div>
                        <div>
                            <h3 className="font-semibold text-red-400">{t('low_certainty')}</h3>
                            <p className="text-gray-300 text-sm">{t('low_certainty_help')}</p>
                        </div>
                    </div>

                    <div className="flex items-start space-x-3">
                        <div className="w-4 h-4 bg-yellow-500 rounded-full mt-1"></div>
                        <div>
                            <h3 className="font-semibold text-yellow-400">{t('medium_certainty')}</h3>
                            <p className="text-gray-300 text-sm">{t('medium_certainty_help')}</p>
                        </div>
                    </div>

                    <div className="flex items-start space-x-3">
                        <div className="w-4 h-4 bg-green-500 rounded-full mt-1"></div>
                        <div>
                            <h3 className="font-semibold text-green-400">{t('high_certainty')}</h3>
                            <p className="text-gray-300 text-sm">{t('high_certainty_help')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Help */}
            <div className="bg-gray-700 rounded-lg p-4">
                <h2 className="text-lg font-semibold text-indigo-400 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 9m0 8V9m0 0L9 7" />
                    </svg>
                    {t('navigation_help')}
                </h2>
                
                <div className="space-y-3 text-gray-300">
                    <div className="flex items-center space-x-3">
                        <span className="px-2 py-1 bg-blue-600 text-white rounded text-sm">{t('theme')}</span>
                        <span className="text-sm">{t('theme_tab_desc')}</span>
                    </div>

                    <div className="flex items-center space-x-3">
                        <span className="px-2 py-1 bg-green-600 text-white rounded text-sm">{t('qcm')}</span>
                        <span className="text-sm">{t('qcm_tab_desc')}</span>
                    </div>

                    <div className="flex items-center space-x-3">
                        <span className="px-2 py-1 bg-purple-600 text-white rounded text-sm">{t('solution')}</span>
                        <span className="text-sm">{t('solution_tab_desc')}</span>
                    </div>

                    <div className="flex items-center space-x-3">
                        <span className="px-2 py-1 bg-yellow-600 text-white rounded text-sm">{t('help')}</span>
                        <span className="text-sm">{t('help_tab_desc')}</span>
                    </div>
                </div>
            </div>

            {/* Question-Specific Help */}
            {(helpSource?.help || currentQCM?.help) && (
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                    <h2 className="text-lg font-semibold text-blue-400 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t('question_help') || 'Question Help'}
                    </h2>
                    
                    <div className="text-gray-300 leading-relaxed">
                        {helpSource?.help || currentQCM?.help}
                    </div>
                </div>
            )}

            {/* QCM-Specific Tips */}
            {currentQCM && currentQCM.metadata && (
                <div className="bg-gray-700 rounded-lg p-4">
                    <h2 className="text-lg font-semibold text-orange-400 mb-3 flex items-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        {t('specific_tips')}
                    </h2>
                    
                    <div className="space-y-3 text-gray-300">
                        {currentQCM.metadata.competency_area && (
                            <div>
                                <span className="font-semibold text-orange-300">{t('competency_area')}:</span>
                                <span className="ml-2">{currentQCM.metadata.competency_area}</span>
                            </div>
                        )}

                        {currentQCM.metadata.concept && (
                            <div>
                                <span className="font-semibold text-orange-300">{t('key_concept')}:</span>
                                <span className="ml-2">{currentQCM.metadata.concept}</span>
                            </div>
                        )}

                        {currentQCM.metadata.concept_estimated_study_time && (
                            <div>
                                <span className="font-semibold text-orange-300">{t('study_time')}:</span>
                                <span className="ml-2">{currentQCM.metadata.concept_estimated_study_time}</span>
                            </div>
                        )}

                        {currentQCM.metadata.components && (
                            <div>
                                <span className="font-semibold text-orange-300">{t('components')}:</span>
                                <div className="ml-2 mt-1 flex flex-wrap gap-1">
                                    {currentQCM.metadata.components.map((component, index) => (
                                        <span key={index} className="px-2 py-1 bg-orange-900/20 border border-orange-500/30 rounded text-xs">
                                            {component}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {currentQCM.metadata.common_issues && (
                            <div>
                                <span className="font-semibold text-orange-300">{t('common_issues')}:</span>
                                <p className="ml-2 text-sm">{currentQCM.metadata.common_issues}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Keyboard Shortcuts */}
            <div className="bg-gray-700 rounded-lg p-4">
                <h2 className="text-lg font-semibold text-pink-400 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4 4 4 0 004-4V5z" />
                    </svg>
                    {t('keyboard_shortcuts')}
                </h2>
                
                <div className="space-y-2 text-sm text-gray-300">
                    <div className="flex justify-between">
                        <span>{t('submit_answer')}:</span>
                        <kbd className="px-2 py-1 bg-gray-600 rounded text-xs">Enter</kbd>
                    </div>
                    <div className="flex justify-between">
                        <span>{t('new_line')}:</span>
                        <kbd className="px-2 py-1 bg-gray-600 rounded text-xs">Shift + Enter</kbd>
                    </div>
                    <div className="flex justify-between">
                        <span>{t('next_question')}:</span>
                        <kbd className="px-2 py-1 bg-gray-600 rounded text-xs">Ctrl + →</kbd>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QCMHelp;